import React from 'react';
import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import { useGlobalization } from '@Translations/useGlobalization';
import { RiskMatrixGrid } from '../RiskMatrixDataGrid';
import { RiskNormalize } from '../RiskNormalize.component';
import { cardSx } from './cardSx';
import { CombineMethodRadios } from './CombineMethodRadios';
import { RiskGradeCard } from './RiskGradeCard';
import { RiskMethodSegmentedControl } from './RiskMethodSegmentedControl';
import { CombineMethod, RiskSetupDraft } from './riskSetupDraft.types';
import { WeightingRow } from './WeightingRow';
import { WorkedExample } from './WorkedExample';

export interface RedesignedRiskGradeLayoutProps {
	isLoading: boolean;
	riskMatrixFlag: boolean;
	riskNormalizeFlag: boolean;
	draft: RiskSetupDraft;
	lofScore: number;
	cofScore: number;
	maxLoFValue: number;
	maxCoFValue: number;
	fullWeight: boolean;
	normalizeSettings: { useNormalize: boolean; normalizeValue: number };
	thresholdsTable: React.ReactNode;
	summary: React.ReactNode;
	belowContent: React.ReactNode;
	onRequestMethodSwitch: () => void;
	onCombineMethodChange: (method: CombineMethod) => void;
	onLofChange: (value: number) => void;
	onCofChange: (value: number) => void;
	onFullWeightChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onNormalizeChanged: (useNormalize: boolean, normalizeValue: number) => void;
}

export const RedesignedRiskGradeLayout = (
	props: RedesignedRiskGradeLayoutProps,
) => {
	const { t } = useGlobalization();
	const { draft, isLoading } = props;
	const showMatrix = props.riskMatrixFlag && draft.riskType === 'matrix';

	const linear = (
		<>
			<Paper elevation={0} sx={cardSx}>
				<Typography
					sx={{
						fontSize: 16,
						fontWeight: 700,
						color: '#0d1520',
						mb: 0.5,
					}}>
					{t('How the risk grade is calculated')}
				</Typography>
				<Typography sx={{ fontSize: 13, color: '#5b6472', mb: 2 }}>
					{t(
						'How should LoF and CoF be combined into a single risk score?',
					)}
				</Typography>
				<CombineMethodRadios
					riskMethod={draft.riskMethod}
					disabled={isLoading}
					onChange={props.onCombineMethodChange}
				/>
				<Divider data-cy="method-weighting-divider" sx={{ my: 3 }} />
				<WeightingRow
					lofValue={draft.lofWeighting}
					cofValue={draft.cofWeighting}
					lofScore={props.lofScore}
					cofScore={props.cofScore}
					maxLoFValue={props.maxLoFValue}
					maxCoFValue={props.maxCoFValue}
					maxScore={draft.maxScore}
					fullWeight={props.fullWeight}
					disabled={isLoading}
					onLofChange={props.onLofChange}
					onCofChange={props.onCofChange}
					onFullWeightChange={props.onFullWeightChange}
				/>
				<WorkedExample
					lofWeighting={draft.lofWeighting}
					cofWeighting={draft.cofWeighting}
					riskMethod={draft.riskMethod}
					thresholds={draft.thresholds}
					useNormalize={draft.useNormalize}
					maxLoFValue={props.maxLoFValue}
					maxCoFValue={props.maxCoFValue}
				/>
			</Paper>
			{props.riskNormalizeFlag && (
				<RiskNormalize
					redesigned
					isLoading={isLoading}
					normalizeSettings={props.normalizeSettings}
					onNormalizeChanged={props.onNormalizeChanged}
				/>
			)}
			<RiskGradeCard
				thresholds={draft.thresholds}
				gradeMax={draft.gradeMax}
				thresholdsTable={props.thresholdsTable}
			/>
		</>
	);

	const matrix = (
		<Paper elevation={0} sx={cardSx}>
			<Typography sx={{ fontSize: 13, color: '#5b6472', mb: 1.5 }}>
				{t(
					// eslint-disable-next-line quotes -- apostrophe; prettier requires double quotes here
					"Use the LoF × CoF matrix to assign a risk grade to each pipe. LoF and CoF weighting and the Add/Multiply settings don't apply to this method.",
				)}
			</Typography>
			<RiskMatrixGrid redesigned />
		</Paper>
	);

	return (
		<Box>
			{props.riskMatrixFlag && (
				<RiskMethodSegmentedControl
					riskType={draft.riskType}
					disabled={isLoading}
					onRequestChange={props.onRequestMethodSwitch}
				/>
			)}
			<Grid container spacing={3}>
				<Grid item xs={12} md={8} lg={9}>
					{showMatrix ? matrix : linear}
					{props.belowContent}
				</Grid>
				<Grid item xs={12} md={4} lg={3}>
					<Box sx={{ position: { md: 'sticky' }, top: 16 }}>
						{props.summary}
					</Box>
				</Grid>
			</Grid>
		</Box>
	);
};
